import { Activity, AlertTriangle } from "lucide-react";
import { MasterShell } from "@/shared/layouts/MasterShell.jsx";
import { Card } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { LoadingState, EmptyState } from "@/shared/ui/spinner.jsx";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/shared/ui/table.jsx";
import { formatNumber } from "@/shared/lib/format.js";
import { StatCard, ClientStatusBadge, UsageBar } from "./components/MasterUi.jsx";
import { useMasterStats, useMasterUsage } from "./use-master.js";

/**
 * Utilização da plataforma por cliente.
 *
 * Compara o consumo real com os limites do plano, destacando quem está
 * perto de estourar. É a informação que sustenta uma conversa de upgrade.
 */
export function UsagePage() {
  const { data: uso = [], isLoading } = useMasterUsage();
  const { data: stats } = useMasterStats();

  if (isLoading) {
    return (
      <MasterShell title="Utilização da Plataforma" subtitle="Consumo por cliente">
        <LoadingState message="Somando o uso..." />
      </MasterShell>
    );
  }

  /** Quem passou de 80% de qualquer limite merece atenção comercial. */
  const noLimite = uso.filter(
    (c) =>
      (c.limitUsers > 0 && c.users / c.limitUsers >= 0.8) ||
      (c.limitMessages > 0 && c.messages / c.limitMessages >= 0.8),
  );

  return (
    <MasterShell
      title="Utilização da Plataforma"
      subtitle="Consumo de cada cliente frente aos limites do plano"
    >
      <div className="space-y-4 p-6">
        {stats && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Usuários" value={formatNumber(stats.usage.usuarios)} accent="primary" />
            <StatCard label="Leads" value={formatNumber(stats.usage.leads)} accent="chart-2" />
            <StatCard label="Mensagens" value={formatNumber(stats.usage.mensagens)} accent="chart-3" />
            <StatCard
              label="Perto do limite"
              value={noLimite.length}
              hint={noLimite.length > 0 ? "oportunidade de upgrade" : "nenhum cliente no limite"}
              icon={AlertTriangle}
              accent={noLimite.length > 0 ? "warning" : "success"}
            />
          </div>
        )}

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-t-0 hover:bg-transparent">
                <TableHead>Cliente</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead className="min-w-40">Usuários</TableHead>
                <TableHead className="min-w-40">Mensagens</TableHead>
                <TableHead>Leads</TableHead>
                <TableHead>Agendados</TableHead>
                <TableHead>Jornadas</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {uso.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState icon={Activity} title="Nenhum cliente cadastrado" />
                  </TableCell>
                </TableRow>
              ) : (
                uso.map((cliente) => (
                  <TableRow key={cliente.id}>
                    <TableCell>
                      <p className="font-medium leading-tight">{cliente.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {cliente.planId ? `plano ${cliente.planId}` : "sem plano"}
                      </p>
                    </TableCell>

                    <TableCell>
                      <ClientStatusBadge status={cliente.status} />
                    </TableCell>

                    <TableCell>
                      <UsageBar value={cliente.users} limit={cliente.limitUsers} label="usuários" />
                    </TableCell>

                    <TableCell>
                      <UsageBar
                        value={cliente.messages}
                        limit={cliente.limitMessages}
                        label="mensagens"
                      />
                    </TableCell>

                    <TableCell className="text-sm">{formatNumber(cliente.leads)}</TableCell>

                    <TableCell>
                      <Badge variant="secondary">{cliente.scheduled}</Badge>
                    </TableCell>

                    <TableCell className="text-sm">{cliente.journeys}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </MasterShell>
  );
}
